/**
 * User Manager — list page (ORCH-336, ORCH-21 §12; PRD User Manager UM-1 to UM-3, UM-13)
 *
 * Purpose: The members of the tenant being managed, within the caller's
 *          `user.view` scope, with their organization, roles, security
 *          attributes, last sign-in to this tenant and status. From here an admin
 *          invites people, opens a person's page, and deactivates, reactivates or
 *          removes them, one at a time or in bulk (two or more ticked rows).
 * Logic: Everything shown and changed is the membership in one tenant: the
 *        active tenant, or the one a system admin in GLOBAL picks. The server
 *        scopes the list, so people outside the caller's scope are neither shown
 *        nor counted, and it says per row what the caller may do (`_authz`).
 * Dependencies: MDTable (metadata mode over `user_tenant`), RoleSelect,
 *               ConfirmDialog, InviteDialog, userManagementService, authzService
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Groups as UsersIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import SkeletonPage from '../../../components/layout/SkeletonPage';
import MDTable from '../../../components/md-table';
import {
  BulkActionBar,
  ConfirmDialog,
  NotificationSnackbar,
  RoleSelect,
  RowActionButton,
  RowActions,
  StatusChip,
} from '../../../components/common';
import InviteDialog from '../../../components/user/InviteDialog';
import {
  attributeValues,
  auditPerson,
  firstLast,
  givenName,
  lastFirst,
  MEMBERSHIP_MODEL_ID,
  membershipStatusLabel,
} from '../../../components/user/membership';
import { useMembershipFields } from '../../../components/user/useMembershipFields';
import { useAuth } from '../../../context/AuthContext';
import { PermissionKey } from '../../../constants/permissionKeys';
import { useDebounce } from '../../../utils/ui';
import { formatAuditTimestamp, formatAuditUser } from '../../../utils/formatting';
import userManagementService, { userErrorMessage } from '../../../services/userManagementService';
import { authzService } from '../../../services/authzService';
import { tenantService } from '../../../services/tenantService';
import { Role } from '../../../types/authz';
import { BulkStatusResult, Member, MemberFilter } from '../../../types/userManagement';
import { EFFECTIVE_ACCESS_TAB_PARAM } from '../RoleManager/RoleManagerPage';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const MAX_ATTRIBUTE_CHIPS = 2;
// Shown on request from the column menu: the table is wide enough without it (ORCH-398)
const HIDDEN_BY_DEFAULT = { security_attributes: false };

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'inactive', label: 'Deactivated' },
];

const SKIP_REASONS: Record<string, string> = {
  LAST_SYSTEM_ADMIN: 'last System admin',
  OUT_OF_SCOPE: 'outside the people you manage',
  INVITE_PENDING: 'invitation not accepted yet',
  NOT_FOUND: 'no longer in this tenant',
};

const users = (count: number) => `${count} ${count === 1 ? 'user' : 'users'}`;

/** "2 users deactivated. 1 skipped (already deactivated)." (PRD UM-13, ORCH-382). */
export const bulkResultMessage = (result: BulkStatusResult, verb: string): string => {
  const message = `${users(result.changed)} ${verb}.`;
  const skipped = result.unchanged + result.skipped.length;
  if (!skipped) return message;
  const reasons = Array.from(
    new Set([
      ...(result.unchanged ? [`already ${verb}`] : []),
      ...result.skipped.map((s) => SKIP_REASONS[s.reason] || s.reason),
    ])
  );
  return `${message} ${skipped} skipped (${reasons.join(', ')}).`;
};

/** Deactivate acts on active members, Reactivate on deactivated ones; the rest are skipped. */
const BULK_VERBS = {
  deactivate: { done: 'deactivated', applies: (status: string) => status === 'active' },
  reactivate: { done: 'reactivated', applies: (status: string) => status === 'inactive' },
} as const;

type BulkKind = keyof typeof BULK_VERBS;

type Confirm =
  | { kind: BulkKind; members: Member[]; allMatching: boolean }
  | { kind: 'remove'; member: Member };

type MemberRow = Member & { id: string; name: string };

const UserManagementPage: React.FC = () => {
  const { state, can } = useAuth();
  const navigate = useNavigate();
  const activeTenantId = state.user?.activeTenant?.tenant_id || '';
  const isSystemAdmin = !!state.user?.is_system_admin;

  // A system admin in GLOBAL picks the tenant to manage; everyone else works in their own
  const [managedTenantId, setManagedTenantId] = useState(activeTenantId);
  const [tenantOptions, setTenantOptions] = useState<{ tenant_id: string; name: string }[]>([]);
  const tenantId = isSystemAdmin ? managedTenantId : activeTenantId;
  const otherTenant = tenantId !== activeTenantId ? tenantId : undefined;
  const tenantName =
    tenantOptions.find((t) => t.tenant_id === tenantId)?.name ||
    (tenantId === activeTenantId ? state.user?.activeTenant?.name : tenantId) ||
    'this tenant';

  const canInvite = can(PermissionKey.USER_CREATE);
  const canDeactivate = can(PermissionKey.USER_DEACTIVATE);
  const canAssignRoles = can(PermissionKey.USER_ASSIGN_ROLES);
  const canExplain = can(PermissionKey.AUDIT_VIEW);

  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [allMatching, setAllMatching] = useState(false);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [working, setWorking] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { attributeFieldIds, valueName } = useMembershipFields(tenantId);

  useEffect(() => {
    setManagedTenantId(activeTenantId);
  }, [activeTenantId]);

  // The tenant list leaves GLOBAL out; a system admin is signed in to it, so it leads the picker
  useEffect(() => {
    if (!isSystemAdmin) return;
    const own = {
      tenant_id: activeTenantId,
      name: state.user?.activeTenant?.name || activeTenantId,
    };
    tenantService
      .getAllTenants()
      .then((tenants) =>
        setTenantOptions([
          own,
          ...tenants
            .filter((t) => t.tenant_id !== activeTenantId)
            .map((t) => ({ tenant_id: t.tenant_id, name: t.name })),
        ])
      )
      .catch(() => setTenantOptions([own]));
  }, [isSystemAdmin, activeTenantId, state.user?.activeTenant?.name]);

  // The managed tenant's role catalog feeds the role filter and the invite dialog's picker
  useEffect(() => {
    authzService
      .listRoles(undefined, otherTenant)
      .then(setRoles)
      .catch(() => setRoles([]));
  }, [activeTenantId, otherTenant]);

  const filter: MemberFilter = useMemo(
    () => ({
      search: search || undefined,
      status: status || undefined,
      role_id: roleId || undefined,
    }),
    [search, status, roleId]
  );

  const fetchMembers = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await userManagementService.listUsers({
        ...filter,
        tenant_id: otherTenant,
        page,
        per_page: perPage,
      });
      setMembers(data.users);
      setTotal(data.total);
      setError(null);
    } catch (e) {
      setError(userErrorMessage(e, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, otherTenant, filter, page, perPage]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // A selection belongs to the rows it was made on
  useEffect(() => {
    setSelectedIds([]);
    setAllMatching(false);
  }, [tenantId, filter, page, perPage]);

  const applySearch = useDebounce((value: string) => {
    setSearch(value.trim());
    setPage(1);
  }, 300);

  const openMember = useCallback(
    (member: Member) => {
      if (member.is_invite) return;
      navigate(
        `/admin/user-management/${member.user_id}${otherTenant ? `?tenant=${encodeURIComponent(otherTenant)}` : ''}`
      );
    },
    [navigate, otherTenant]
  );

  const openEffectiveAccess = useCallback(
    (member: Member) =>
      navigate(`/admin/role-manager?tab=${EFFECTIVE_ACCESS_TAB_PARAM}`, {
        state: {
          member: {
            user_id: member.user_id,
            email: member.email,
            first_name: member.first_name ?? null,
            last_name: member.last_name ?? null,
            ...(otherTenant ? { tenant_id: otherTenant, tenant_name: tenantName } : {}),
          },
        },
      }),
    [navigate, otherTenant, tenantName]
  );

  const reactivate = useCallback(
    async (member: Member) => {
      try {
        await userManagementService.setActive(member.user_id, tenantId, true);
        setNotice(`${givenName(member)} reactivated in ${tenantName}.`);
        fetchMembers();
      } catch (e) {
        setError(userErrorMessage(e, 'Could not reactivate this person.'));
      }
    },
    [tenantId, tenantName, fetchMembers]
  );

  const handleConfirm = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      if (confirm.kind === 'remove') {
        await userManagementService.removeMember(confirm.member.user_id, tenantId);
        setNotice(`${firstLast(confirm.member)} removed from ${tenantName}.`);
      } else if (confirm.members.length === 1 && !confirm.allMatching) {
        await userManagementService.setActive(confirm.members[0].user_id, tenantId, false);
        setNotice(`${givenName(confirm.members[0])} deactivated in ${tenantName}.`);
      } else {
        // The server says per person what changed; the selection is sent whole
        const result = await userManagementService.bulkSetActive(
          confirm.allMatching ? filter : confirm.members.map((m) => m.user_id),
          confirm.kind === 'reactivate',
          otherTenant
        );
        setNotice(bulkResultMessage(result, BULK_VERBS[confirm.kind].done));
      }
      setSelectedIds([]);
      setAllMatching(false);
      setConfirm(null);
      fetchMembers();
    } catch (e) {
      setConfirm(null);
      setError(userErrorMessage(e, 'The change could not be made.'));
    } finally {
      setWorking(false);
    }
  };

  const rows: MemberRow[] = useMemo(
    () => members.map((m) => ({ ...m, id: m.user_id, name: lastFirst(m) })),
    [members]
  );

  const columnsBefore: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 160,
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2" data-testid="user-name">
            {(params.row as MemberRow).name}
          </Typography>
        ),
      },
      { field: 'email', headerName: 'Email', minWidth: 190, flex: 1 },
    ],
    []
  );

  const columnsAfter: GridColDef[] = useMemo(
    () => [
      {
        field: 'roles',
        headerName: 'Roles',
        minWidth: 170,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const held = (params.row as MemberRow).roles;
          if (!held.length) {
            return (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            );
          }
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
              {held.map((role) => (
                <Tooltip key={role.role_id} title={role.description || ''}>
                  <Chip size="small" label={role.name} data-testid="user-role-chip" />
                </Tooltip>
              ))}
            </Box>
          );
        },
      },
      {
        field: 'security_attributes',
        headerName: 'Security Attributes',
        minWidth: 220,
        sortable: false,
        renderCell: (params) => {
          const names = attributeValues((params.row as MemberRow).metadata).map((v) =>
            valueName(v.fielddefId, v.valueId)
          );
          if (!names.length) {
            return (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            );
          }
          return (
            <Tooltip title={names.join(', ')}>
              <Box sx={{ display: 'flex', gap: 0.5 }} data-testid="user-attributes">
                {names.slice(0, MAX_ATTRIBUTE_CHIPS).map((name, index) => (
                  <Chip key={`${name}-${index}`} size="small" variant="outlined" label={name} />
                ))}
                {names.length > MAX_ATTRIBUTE_CHIPS && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`+${names.length - MAX_ATTRIBUTE_CHIPS}`}
                  />
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'last_login_at',
        headerName: 'Last Sign-In',
        width: 140,
        valueGetter: (_value, row) =>
          row.last_login_at ? formatAuditTimestamp(row.last_login_at) : '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 105,
        renderCell: (params) => {
          const memberStatus = (params.row as MemberRow).status;
          return <StatusChip status={memberStatus} label={membershipStatusLabel(memberStatus)} />;
        },
      },
      {
        field: 'updated_at',
        headerName: 'Modified',
        width: 140,
        valueGetter: (_value, row) => (row.updated_at ? formatAuditTimestamp(row.updated_at) : '—'),
      },
      {
        field: 'updated_by',
        headerName: 'Modified By',
        width: 120,
        valueGetter: (_value, row) =>
          formatAuditUser(row.updated_by, auditPerson(row.updated_by_user)),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const member = params.row as MemberRow;
          if (member.is_invite) return null;
          const mayDeactivate = canDeactivate && member._authz?.can_deactivate;
          return (
            <RowActions
              entityLabel="User"
              rowLabel={member.email}
              testIdPrefix="user-action"
              rowTestId={`user-actions-${member.email}`}
              onEdit={() => openMember(member)}
              secondaryActions={
                canExplain && (
                  <RowActionButton
                    label="Effective Access"
                    testId="user-action-effective-access"
                    onClick={() => openEffectiveAccess(member)}
                    icon={<VisibilityIcon fontSize="small" />}
                  />
                )
              }
              deactivation={
                mayDeactivate
                  ? {
                      variant: 'deactivate',
                      isActive: member.status !== 'inactive',
                      onToggle: () =>
                        member.status === 'inactive'
                          ? reactivate(member)
                          : setConfirm({
                              kind: 'deactivate',
                              members: [member],
                              allMatching: false,
                            }),
                    }
                  : undefined
              }
              onDelete={mayDeactivate ? () => setConfirm({ kind: 'remove', member }) : undefined}
              deleteVerb="Remove"
            />
          );
        },
      },
    ],
    [canDeactivate, canExplain, openMember, openEffectiveAccess, reactivate, valueName]
  );

  // Memoized: a fresh object every render re-runs MDTable's effects
  const mdTableMetadata = useMemo(
    () => ({
      modelId: MEMBERSHIP_MODEL_ID,
      viewType: 'table' as const,
      tenantId,
      title: `Users — ${total} in ${tenantName}`,
      columnsBeforeRelatedFields: columnsBefore,
      columnsAfterRelatedFields: columnsAfter,
    }),
    [tenantId, tenantName, total, columnsBefore, columnsAfter]
  );

  const customToolbarFilters = (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {isSystemAdmin && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="user-tenant-label">Tenant</InputLabel>
          <Select
            labelId="user-tenant-label"
            label="Tenant"
            value={tenantOptions.some((t) => t.tenant_id === tenantId) ? tenantId : ''}
            onChange={(event) => {
              setManagedTenantId(event.target.value);
              setRoleId(null);
              setPage(1);
            }}
            data-testid="user-tenant-picker"
          >
            {tenantOptions.map((tenant) => (
              <MenuItem key={tenant.tenant_id} value={tenant.tenant_id}>
                {tenant.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <TextField
        size="small"
        label="Search"
        placeholder="Name or email"
        value={searchInput}
        onChange={(event) => {
          setSearchInput(event.target.value);
          applySearch(event.target.value);
        }}
        sx={{ minWidth: 240 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: searchInput ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setPage(1);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        inputProps={{ 'data-testid': 'user-search' }}
      />
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="user-status-label" shrink>
          Status
        </InputLabel>
        <Select
          labelId="user-status-label"
          label="Status"
          notched
          displayEmpty
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
          data-testid="user-status-filter"
        >
          {STATUS_FILTERS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <RoleSelect
        multiple={false}
        offerAll
        roles={roles}
        value={roleId}
        onChange={(next) => {
          setRoleId(next);
          setPage(1);
        }}
        label="Role"
        placeholder="All Roles"
        size="small"
        dataTestId="user-role-filter"
        sx={{ minWidth: 220 }}
      />
    </Box>
  );

  const selectedMembers = members.filter((m) => selectedIds.includes(m.user_id));
  const selectedCount = allMatching ? total : selectedIds.length;
  const pageFullySelected =
    !allMatching &&
    total > members.length &&
    selectedIds.length > 0 &&
    members
      .filter((m) => !m.is_invite && m._authz?.can_deactivate)
      .every((m) => selectedIds.includes(m.user_id));

  // An action is offered only when it applies to someone selected. With every match selected
  // the unloaded rows are unknown, so only the Status filter can rule an action out.
  const bulkApplies = (kind: BulkKind): boolean =>
    allMatching
      ? !status || BULK_VERBS[kind].applies(status)
      : selectedMembers.some((m) => BULK_VERBS[kind].applies(m.status));

  const selectionBar = () => (
    <BulkActionBar
      selectedCount={selectedCount}
      working={working}
      testIdPrefix="user-bulk"
      onClear={() => {
        setSelectedIds([]);
        setAllMatching(false);
      }}
      actions={[
        {
          label: 'Deactivate',
          disabled: !bulkApplies('deactivate'),
          disabledReason: 'No active users selected',
          onClick: () => setConfirm({ kind: 'deactivate', members: selectedMembers, allMatching }),
          testId: 'user-bulk-deactivate',
          destructive: true,
        },
        {
          label: 'Reactivate',
          disabled: !bulkApplies('reactivate'),
          disabledReason: 'No deactivated users selected',
          onClick: () => setConfirm({ kind: 'reactivate', members: selectedMembers, allMatching }),
          testId: 'user-bulk-reactivate',
        },
      ]}
    >
      {pageFullySelected && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setAllMatching(true)}
          data-testid="user-select-all-matching"
        >
          Select all {total} matching your filters
        </Link>
      )}
    </BulkActionBar>
  );

  // The bulk dialog names who is affected and who is skipped: "Deactivate 2 users? 1 already
  // deactivated will be skipped." A row's own Deactivate names the person.
  const bulk = confirm && confirm.kind !== 'remove' ? confirm : null;
  const single = bulk && !bulk.allMatching && bulk.members.length === 1 ? bulk.members[0] : null;
  const affected = bulk
    ? bulk.members.filter((m) => BULK_VERBS[bulk.kind].applies(m.status)).length
    : 0;
  const bulkSkipped = bulk && !bulk.allMatching ? bulk.members.length - affected : 0;
  const confirmWho =
    confirm?.kind === 'remove'
      ? firstLast(confirm.member)
      : single
        ? firstLast(single)
        : users(bulk?.allMatching ? total : affected);
  const bulkSkipNote =
    bulk && bulkSkipped
      ? ` ${bulkSkipped} already ${BULK_VERBS[bulk.kind].done} will be skipped.`
      : '';
  // Wording from QA (ORCH-382, 2026-10-21): the full name as in the title, what they lose, what is
  // kept. The row's trash icon reads "Remove User" and is offered with the same right as Deactivate,
  // so a row's dialog can always name it; the bulk dialog has no remove and says who is skipped.
  const deactivateText = single
    ? `${firstLast(single)} will no longer be able to access ${tenantName}. Their profile, roles, ` +
      `and security attributes will be kept, and you can reactivate them at any time. ` +
      `To remove them from ${tenantName} instead, use Remove User.`
    : `They will no longer be able to access ${tenantName}. Their profiles, roles, and security ` +
      `attributes will be kept, and you can reactivate them at any time.${bulkSkipNote}`;

  return (
    <SkeletonPage
      pageTitle="User Manager"
      breadcrumbs={[
        { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
        { label: 'Admin', href: '/admin', icon: <SettingsIcon fontSize="inherit" /> },
        { label: 'User Manager', icon: <UsersIcon fontSize="inherit" /> },
      ]}
    >
      <Box>
        <MDTable
          mode="metadata"
          metadata={mdTableMetadata}
          data={rows}
          loading={loading}
          error={error}
          onRowClick={openMember}
          // An invite has no user page yet: its name stays plain text
          isRowClickable={(row) => !row.is_invite}
          defaultColumnVisibility={HIDDEN_BY_DEFAULT}
          selectable={canDeactivate}
          isRowSelectable={(row) => !row.is_invite && !!row._authz?.can_deactivate}
          selectedIds={selectedIds}
          onSelectionChange={(ids) => {
            setSelectedIds(ids);
            setAllMatching(false);
          }}
          selectionBar={selectionBar}
          sortable
          filterable
          showRefreshButton
          showAddButton={false}
          onRefresh={fetchMembers}
          customToolbarFilters={customToolbarFilters}
          customHeaderActions={
            canInvite ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => setInviteOpen(true)}
                data-testid="user-invite-button"
              >
                + Invite
              </Button>
            ) : undefined
          }
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            gap: 2,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="user-page-size-label">Rows Per Page</InputLabel>
            <Select
              labelId="user-page-size-label"
              value={String(perPage)}
              label="Rows Per Page"
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={String(size)}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Pagination
            page={page}
            count={Math.max(1, Math.ceil(total / perPage))}
            onChange={(_event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      </Box>

      <InviteDialog
        open={inviteOpen}
        tenantId={tenantId}
        targetTenantId={otherTenant}
        roles={roles}
        attributeFieldIds={attributeFieldIds}
        canAssignRoles={canAssignRoles}
        onClose={() => setInviteOpen(false)}
        onInvited={(summary) => {
          setInviteOpen(false);
          setNotice(summary);
          fetchMembers();
        }}
      />

      <ConfirmDialog
        open={confirm?.kind === 'deactivate'}
        title={`Deactivate ${confirmWho}?`}
        confirmLabel="Deactivate"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="deactivate-dialog"
      >
        <Typography variant="body2">{deactivateText}</Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm?.kind === 'reactivate'}
        title={`Reactivate ${confirmWho}?`}
        confirmLabel="Reactivate"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="reactivate-dialog"
      >
        <Typography variant="body2">
          They can sign in to {tenantName} again with the roles and security attributes they had.
          {bulkSkipNote}
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm?.kind === 'remove'}
        title={`Remove ${confirmWho}?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="remove-dialog"
      >
        <Typography variant="body2">
          {confirm?.kind === 'remove' ? givenName(confirm.member) : 'This person'} loses all access
          to {tenantName}. Their history here is kept. To pause access instead, use Deactivate.
        </Typography>
      </ConfirmDialog>

      <NotificationSnackbar
        open={!!error && !loading}
        onClose={() => setError(null)}
        message={error || ''}
        severity="error"
        autoHideDuration={6000}
      />
      <NotificationSnackbar
        open={!!notice}
        onClose={() => setNotice(null)}
        message={notice || ''}
        severity="success"
        autoHideDuration={5000}
      />
    </SkeletonPage>
  );
};

export default UserManagementPage;
